import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { Color } from '../../../model';

/**
 * Colour swatch like {@link LabColorPreviewComponent}, but the hover/focus
 * tooltip also shows the colorant FORMULATION when a {@link Color} is provided.
 *
 * Inputs:
 * - `lab`   CIE-Lab colour. `null` / `undefined` → renders nothing.
 * - `color` optional {@link Color}. When present (and it has at least one
 *           non-zero colorant), the tooltip appends a FORMULATION block.
 *           `null` / `undefined` → no FORMULATION part shown.
 *
 * Formulation = every single-letter colorant field of {@link Color}
 * (a,b,c,d,e,f,i,j,k,l,m,n,o,r,v,y) whose value is neither 0 nor null. Each
 * value is the number of drops of that colorant for a 3 kg bucket. The 24 kg
 * line is the same values × 8. Values rounded to one decimal.
 *
 * Tooltip format:
 * ```
 * LAB — L: 84.4, A: 2.2, B: 7.2
 * -------FORMULATION-------
 * 3kg:  A:1.0 B:2.5 D:0.3
 * 24kg: A:8.0 B:20.0 D:2.4
 * ```
 *
 * Standalone — import directly (`imports: [FormulationColorPreviewComponent]`).
 */
@Component({
  selector: 'app-formulation-color-preview',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NzTooltipModule],
  template: `
    @if (lab(); as l) {
      <span
        class="swatch"
        role="img"
        [attr.aria-label]="ariaLabel()"
        [style.background]="cssColor()"
        nz-tooltip
        [nzTooltipTitle]="tooltip"></span>

      <ng-template #tooltip>
        <div class="tt-line">{{ labLine() }}</div>
        @if (formulation().length > 0) {
          <div class="tt-sep">-------FORMULATION-------</div>
          <div class="tt-line">3kg:&nbsp; {{ drops3kg() }}</div>
          <div class="tt-line">24kg: {{ drops24kg() }}</div>
        }
      </ng-template>
    }
  `,
  styles: [`
    .swatch {
      display: inline-block;
      width: 16px;
      height: 16px;
      border-radius: 3px;
      border: 1px solid rgba(0, 0, 0, 0.15);
      vertical-align: middle;
      cursor: help;
    }
    .tt-line { white-space: nowrap; font-variant-numeric: tabular-nums; }
    .tt-sep  { white-space: nowrap; opacity: 0.8; }
  `],
})
export class FormulationColorPreviewComponent {
  /** Color to preview. `null` / `undefined` ⇒ renders nothing. `color.lab` drives the swatch. */
  readonly color = input<Color | null | undefined>(undefined);

  /** Derived from `color.lab`. */
  readonly lab = computed(() => this.color()?.lab ?? null);

  /**
   * Single-letter colorant property names in the {@link Color} interface,
   * in declaration order. These are the drop-count fields shown in the
   * formulation. Non-colorant single-letter fields are NOT in here.
   */
  private static readonly COLORANT_KEYS: readonly (keyof Color)[] =
    ['a', 'b', 'c', 'd', 'e', 'f', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'r', 'v', 'y'];

  /** `lab()` CSS colour string for the swatch background. */
  readonly cssColor = computed((): string => {
    const l = this.lab();
    return l ? `lab(${l.l} ${l.a} ${l.b})` : '';
  });

  /** The "LAB — L: …, A: …, B: …" line (also reused in the aria-label). */
  readonly labLine = computed((): string => {
    const l = this.lab();
    return l ? `LAB — L: ${l.l.toFixed(1)}, A: ${l.a.toFixed(1)}, B: ${l.b.toFixed(1)}` : '';
  });

  /** Non-zero colorants as {letter, drops} pairs (drops = 3 kg value). */
  readonly formulation = computed((): { letter: string; drops: number }[] => {
    const c = this.color();
    if (!c) return [];
    const out: { letter: string; drops: number }[] = [];
    for (const key of FormulationColorPreviewComponent.COLORANT_KEYS) {
      const v = c[key] as unknown as number | null | undefined;
      if (v != null && v !== 0) {
        out.push({ letter: (key as string).toUpperCase(), drops: v });
      }
    }
    return out;
  });

  /** "A:1.0 B:2.5 …" for a 3 kg bucket. */
  readonly drops3kg = computed((): string =>
    this.formulation().map(f => `${f.letter}:${f.drops.toFixed(1)}`).join(' '));

  /** "A:8.0 B:20.0 …" for a 24 kg bucket (3 kg values × 8). */
  readonly drops24kg = computed((): string =>
    this.formulation().map(f => `${f.letter}:${(f.drops * 8).toFixed(1)}`).join(' '));

  /** Accessible label — single line combining LAB + formulation (if any). */
  readonly ariaLabel = computed((): string => {
    const lab = this.labLine();
    if (!lab) return '';
    const f = this.formulation();
    if (f.length === 0) return lab;
    return `${lab}. Formulation 3kg: ${this.drops3kg()}; 24kg: ${this.drops24kg()}`;
  });
}
